import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getUser } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";

export default function Admin() {
  const nav = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({});
  const [draftForm, setDraftForm] = useState({domain:"logical_reasoning", layer:"cognitive", difficulty:3, count:3, notes:""});
  const [drafting, setDrafting] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [codes, setCodes] = useState([]);
  const [codeForm, setCodeForm] = useState({ scope: "unlock_report", max_uses: 1, quantity: 5, note: "", expires_in_days: "" });
  const [minting, setMinting] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "SUPER_ADMIN") { nav("/auth"); return; }
    (async () => {
      try {
        const [a, q, d] = await Promise.all([api.get("/admin/analytics"), api.get("/admin/questions"), api.get("/admin/ai/drafts")]);
        setAnalytics(a.data); setQuestions(q.data.questions); setStats(q.data.stats); setDrafts(d.data.drafts);
      } catch { toast.error("Admin load failed. Are you signed in as admin?"); }
    })();
  }, [nav, user]);

  const toggle = async (qid) => {
    const { data } = await api.post(`/admin/questions/${qid}/toggle`);
    setQuestions(qs => qs.map(q => q.question_id === qid ? { ...q, active_status: data.active_status } : q));
  };

  const loadCodes = async () => {
    try {
      const { data } = await api.get("/admin/codes");
      setCodes(data.codes);
    } catch { toast.error("Failed to load codes"); }
  };

  const mintCodes = async () => {
    setMinting(true);
    try {
      const payload = { ...codeForm };
      const days = Number(payload.expires_in_days);
      if (days > 0) {
        const expires = new Date();
        expires.setDate(expires.getDate() + days);
        payload.expires_at = expires.toISOString();
      }
      delete payload.expires_in_days;
      const { data } = await api.post("/admin/codes", payload);
      toast.success(`Minted ${data.count} code${data.count === 1 ? "" : "s"}${days > 0 ? ` · expire in ${days}d` : ""}`);
      await loadCodes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mint failed"); }
    finally { setMinting(false); }
  };

  const toggleCode = async (c) => {
    try {
      await api.post(`/admin/codes/${c}/toggle`);
      await loadCodes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const copyCode = async (c) => {
    try { await navigator.clipboard.writeText(c); toast.success(`Copied ${c}`); }
    catch { toast.error("Copy failed"); }
  };

  useEffect(() => { if (tab === "codes") loadCodes(); }, [tab]);

  const downloadResearchExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/research/export", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : `dualq_research_export_${Date.now()}.zip`;      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Research export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div><Nav />
      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">Admin</div>
        <h1 className="font-serif text-4xl mb-6" data-testid="admin-title">DualQ Console</h1>
        <div className="flex gap-2 mb-6 flex-wrap">
          {["analytics","questions","ai_drafter","research","codes"].map(t => (
            <button key={t} onClick={()=>setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm ${tab===t?"bg-[#E0A96D] text-[#070C18] font-semibold":"border border-slate-700 text-slate-300 hover:border-slate-500"}`}
              data-testid={`admin-tab-${t}`}>
              {t === "ai_drafter" ? "AI drafter" : t === "codes" ? "Access codes" : t}
            </button>
          ))}
          <button onClick={()=>nav("/admin/drafts")}
            className="px-4 py-2 rounded-lg text-sm border border-slate-700 text-slate-300 hover:border-slate-500"
            data-testid="admin-tab-bank-drafts">
            Bank drafts
          </button>
        </div>

        {tab === "analytics" && analytics && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-analytics">
            {[
              ["Total users", analytics.total_users],
              ["Assessments started", analytics.total_started],
              ["Assessments completed", analytics.total_completed],
              ["Profiles unlocked", analytics.total_unlocked],
              ["Completion rate", `${Math.round(analytics.completion_rate*100)}%`],
              ["Unlock conversion", `${Math.round(analytics.unlock_rate*100)}%`],
            ].map(([l,v]) => (
              <div key={l} className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5">
                <div className="text-xs uppercase font-mono tracking-widest text-slate-500">{l}</div>
                <div className="font-serif text-3xl mt-2">{v}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "questions" && (
          <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 overflow-hidden overflow-x-auto" data-testid="admin-questions-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-[#0E172A] text-xs uppercase font-mono text-slate-500">
                <tr>
                  <th className="text-left p-3">ID</th><th className="text-left p-3">Layer</th>
                  <th className="text-left p-3">Domain</th><th className="text-right p-3">Diff</th>
                  <th className="text-right p-3">N</th><th className="text-right p-3">Correct%</th>
                  <th className="text-right p-3">Avg ms</th><th className="text-right p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {questions.map(q => {
                  const s = stats[q.question_id] || {};
                  return (
                    <tr key={q.question_id} className="border-t border-slate-800/70">
                      <td className="p-3 font-mono text-xs">{q.question_id}</td>
                      <td className="p-3">{q.layer}</td>
                      <td className="p-3 capitalize">{q.domain.replace(/_/g," ")}</td>
                      <td className="p-3 text-right font-mono">{q.difficulty_estimate}</td>
                      <td className="p-3 text-right font-mono">{s.n || 0}</td>
                      <td className="p-3 text-right font-mono">{s.correct_rate != null ? Math.round(s.correct_rate*100)+"%" : "—"}</td>
                      <td className="p-3 text-right font-mono">{s.avg_time || "—"}</td>
                      <td className="p-3 text-right">
                        <button onClick={()=>toggle(q.question_id)}
                          className={`px-2 py-1 rounded text-xs ${q.active_status?"bg-emerald-500/20 text-emerald-300":"bg-slate-700 text-slate-400"}`}
                          data-testid={`toggle-${q.question_id}`}>
                          {q.active_status?"active":"inactive"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "ai_drafter" && (
          <div className="space-y-6" data-testid="admin-ai-drafter">
            <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5">
              <div className="font-serif text-xl mb-3">Draft new questions with AI</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <select value={draftForm.layer} onChange={e=>setDraftForm({...draftForm, layer:e.target.value})} className="px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 text-sm" data-testid="draft-layer">
                  <option value="cognitive">cognitive</option>
                  <option value="emotional">emotional</option>
                </select>
                <input value={draftForm.domain} onChange={e=>setDraftForm({...draftForm, domain:e.target.value})}
                  placeholder="domain (e.g. logical_reasoning)" className="px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 text-sm" data-testid="draft-domain" />
                <select value={draftForm.difficulty} onChange={e=>setDraftForm({...draftForm, difficulty:Number(e.target.value)})} className="px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 text-sm" data-testid="draft-difficulty">
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>difficulty {n}/5</option>)}
                </select>
                <select value={draftForm.count} onChange={e=>setDraftForm({...draftForm, count:Number(e.target.value)})} className="px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 text-sm" data-testid="draft-count">
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} drafts</option>)}
                </select>
              </div>
              <textarea rows={2} placeholder="Optional guidance for the AI (e.g. 'make the setup about everyday workplace situations')"
                value={draftForm.notes} onChange={e=>setDraftForm({...draftForm, notes:e.target.value})}
                className="mt-3 w-full px-3 py-2 rounded-lg bg-[#0E172A] border border-slate-700 text-sm" data-testid="draft-notes" />
              <button onClick={async ()=>{
                setDrafting(true);
                try {
                  const { data } = await api.post("/admin/ai/draft-questions", draftForm);
                  toast.success(`${data.drafts.length} drafts generated`);
                  const d = await api.get("/admin/ai/drafts"); setDrafts(d.data.drafts);
                } catch(e) { toast.error(e?.response?.data?.detail || "Draft failed"); }
                finally { setDrafting(false); }
              }} disabled={drafting} className="mt-3 px-5 py-2 rounded-lg bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60" data-testid="draft-generate">
                {drafting ? "Drafting with Claude…" : "Generate drafts"}
              </button>
            </div>

            <div className="space-y-3" data-testid="drafts-list">
              <div className="text-sm font-mono text-slate-500 uppercase tracking-widest">Drafts queue</div>
              {drafts.length === 0 && <div className="text-slate-500 text-sm">No drafts yet.</div>}
              {drafts.map(d => (
                <div key={d.id} className="rounded-xl bg-[#131E36] border border-slate-800/70 p-4" data-testid={`draft-${d.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="font-mono text-xs text-slate-500">
                      {d.layer} · {d.domain} · diff {d.difficulty_estimate}/5 · <span className={d.approval_status==="pending"?"text-amber-300":d.approval_status==="approved"?"text-emerald-300":"text-rose-300"}>{d.approval_status}</span>
                    </div>
                    {d.approval_status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={async ()=>{
                          try { await api.post("/admin/ai/approve", { draft_id: d.id }); toast.success("Approved & added to bank"); const r = await api.get("/admin/ai/drafts"); setDrafts(r.data.drafts); } catch { toast.error("Approve failed"); }
                        }} className="px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs" data-testid={`approve-${d.id}`}>Approve</button>
                        <button onClick={async ()=>{
                          try { await api.post("/admin/ai/reject", { draft_id: d.id }); toast.success("Rejected"); const r = await api.get("/admin/ai/drafts"); setDrafts(r.data.drafts); } catch { toast.error("Reject failed"); }
                        }} className="px-3 py-1 rounded-lg bg-rose-500/20 text-rose-300 text-xs" data-testid={`reject-${d.id}`}>Reject</button>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-slate-200 mb-2">{d.prompt}</div>
                  <ol className="space-y-1 text-sm text-slate-400 pl-5">
                    {(d.answer_options||[]).map((o,i) => (
                      <li key={i} className={i===d.correct_answer ? "text-emerald-300 list-disc" : "list-disc"}>{o}</li>
                    ))}
                  </ol>
                  {d.explanation && <div className="mt-2 text-xs text-slate-500 italic">"{d.explanation}"</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "research" && (
          <div className="space-y-6" data-testid="admin-research">
            <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6">
              <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-2">Independent validation</div>
              <h2 className="font-serif text-2xl mb-2">Research data export</h2>
              <p className="text-sm text-slate-400 mb-4 max-w-2xl">
                Download a ZIP containing anonymized item responses, assessment summaries, the active question bank
                and any paired external-test records collected to date. All user and assessment identifiers are
                replaced with deterministic salted SHA-256 hashes. No emails, names or payment identifiers are included.
                Use this bundle to share with external psychometricians for independent reliability and validity review.
              </p>
              <ul className="text-xs text-slate-500 space-y-1 mb-5 list-disc pl-5">
                <li><span className="font-mono text-slate-300">dualQ_assessment_responses.csv</span> — one row per answered item</li>
                <li><span className="font-mono text-slate-300">dualQ_question_bank.csv</span> — item metadata with p-values and mean response time</li>
                <li><span className="font-mono text-slate-300">dualQ_assessment_summary.csv</span> — one row per completed assessment with domain + index scores</li>
                <li><span className="font-mono text-slate-300">dualQ_reference_validation.csv</span> — paired external-measure records</li>
                <li><span className="font-mono text-slate-300">data_quality_report.txt</span> — sample sizes and calibration readiness</li>
              </ul>
              <button
                onClick={downloadResearchExport}
                disabled={exporting}
                className="px-5 py-2 rounded-lg bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60"
                data-testid="research-export-btn"
              >
                {exporting ? "Preparing export…" : "Download research export (.zip)"}
              </button>
            </div>
          </div>
        )}

        {tab === "codes" && (
          <div className="space-y-6" data-testid="admin-codes">
            <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6">
              <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-2">Free unlocks</div>
              <h2 className="font-serif text-2xl mb-2">Mint access codes</h2>
              <p className="text-sm text-slate-400 mb-4 max-w-2xl">
                Hand these out to friends, family or testers. Each code grants a free unlock of the complete profile
                (<span className="font-mono">unlock_report</span>) or 30 days of DualQ Plus (<span className="font-mono">plus_30d</span>, no auto-renew).
              </p>
              <div className="grid gap-3 sm:grid-cols-5 mb-4">
                <select value={codeForm.scope} onChange={e => setCodeForm(f => ({ ...f, scope: e.target.value }))} className="bg-[#0E172A] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="code-scope">
                  <option value="unlock_report">Unlock report ($1.99)</option>
                  <option value="plus_30d">30 days of Plus</option>
                </select>
                <input type="number" min="1" max="10000" value={codeForm.max_uses} onChange={e => setCodeForm(f => ({ ...f, max_uses: Number(e.target.value) }))} placeholder="Max uses per code" className="bg-[#0E172A] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="code-max-uses" />
                <input type="number" min="1" max="500" value={codeForm.quantity} onChange={e => setCodeForm(f => ({ ...f, quantity: Number(e.target.value) }))} placeholder="How many codes" className="bg-[#0E172A] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="code-quantity" />
                <input type="number" min="0" max="3650" value={codeForm.expires_in_days} onChange={e => setCodeForm(f => ({ ...f, expires_in_days: e.target.value }))} placeholder="Expires in … days (blank = never)" className="bg-[#0E172A] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="code-expires-days" />
                <input type="text" value={codeForm.note} onChange={e => setCodeForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (e.g. 'Family')" className="bg-[#0E172A] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="code-note" />
              </div>
              <button onClick={mintCodes} disabled={minting} className="px-5 py-2 rounded-lg bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60" data-testid="mint-codes-btn">
                {minting ? "Minting…" : `Mint ${codeForm.quantity} code${codeForm.quantity === 1 ? "" : "s"}`}
              </button>
            </div>

            <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-lg">All codes ({codes.length})</h3>
                <button onClick={loadCodes} className="text-xs text-slate-400 hover:text-slate-200" data-testid="refresh-codes">Refresh</button>
              </div>
              {codes.length === 0 ? (
                <div className="text-slate-500 text-sm">No codes yet. Mint one above.</div>
              ) : (
                <div className="space-y-2" data-testid="codes-list">
                  {codes.map(c => (
                    <div key={c.id} className="flex items-center gap-3 flex-wrap rounded-xl bg-[#0E172A] border border-slate-800/60 px-3 py-2 text-sm" data-testid={`code-${c.code}`}>
                      <button onClick={() => copyCode(c.code)} className="font-mono text-[#F0C28B] hover:text-white tracking-widest" title="Copy">
                        {c.code}
                      </button>
                      <span className="text-xs text-slate-500">{c.scope === "unlock_report" ? "Unlock report" : "Plus 30d"}</span>
                      <span className="text-xs text-slate-500">· {c.times_used}/{c.max_uses} used</span>
                      {c.expires_at && <span className="text-xs text-slate-500">· expires {new Date(c.expires_at).toLocaleDateString()}</span>}
                      {c.note && <span className="text-xs text-slate-500 italic">· {c.note}</span>}
                      <span className={`ml-auto text-xs px-2 py-0.5 rounded border ${c.active ? "text-emerald-200 border-emerald-500/40 bg-emerald-500/10" : "text-slate-400 border-slate-700 bg-slate-800/30"}`}>
                        {c.active ? "active" : "inactive"}
                      </span>
                      <button onClick={() => toggleCode(c.code)} className="text-xs text-slate-300 hover:text-white underline" data-testid={`toggle-${c.code}`}>
                        {c.active ? "Deactivate" : "Reactivate"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
